const MULTILINE_ENVIRONMENT_PATTERN =
  /\\begin\{(matrix|pmatrix|bmatrix|Bmatrix|vmatrix|Vmatrix|smallmatrix|array|cases|align\*?|aligned|eqnarray\*?|flalign\*?|gather\*?|gathered|split|multline\*?)\}([\s\S]*?)\\end\{\1\}/g;
const MARKDOWN_ESCAPED_LATEX_COMMAND_PATTERN = /(?<!\\)\\\\(?=[A-Za-z])/g;
const MARKDOWN_ESCAPED_ROW_BREAK_PATTERN = /(?<!\\)\\\\\\\\(?!\\)/g;

const repairFlattenedEnvironmentRows = (expression: string): string =>
  expression.replace(MULTILINE_ENVIRONMENT_PATTERN, (_environmentExpression, environment: string, content: string) => {
    const repairedContent = content.replace(/(?<!\\)\\(?:space|[ \t\r\n]+)/g, '\\\\ ');

    return `\\begin{${environment}}${repairedContent}\\end{${environment}}`;
  });

export const normalizeEscapedLatexCommands = (expression: string): string => expression.replace(/(^|[\s{])\\\\(?=[A-Za-z])/g, '$1\\');

/**
 * Repairs unbalanced grouping braces, which models intermittently emit (an extra trailing `}` or a
 * missing closer) and which KaTeX's lenient fallback renders as raw error-colored text. Unmatched
 * closers are dropped and missing closers appended; balanced input, including escaped `\{`/`\}`,
 * is returned unchanged.
 */
const balanceBraces = (expression: string): string => {
  let depth = 0;
  let droppedCloser = false;
  const kept: string[] = [];

  for (let index = 0; index < expression.length; index += 1) {
    const char = expression[index] as string;
    if (char === '\\') {
      kept.push(char);
      if (index + 1 < expression.length) {
        kept.push(expression[index + 1] as string);
        index += 1;
      }
      continue;
    }
    if (char === '{') depth += 1;
    if (char === '}') {
      if (depth === 0) {
        droppedCloser = true;
        continue;
      }
      depth -= 1;
    }
    kept.push(char);
  }

  if (!droppedCloser && depth === 0) return expression;
  return kept.join('') + '}'.repeat(depth);
};

export const normalizeLatexExpression = (expression: string): string =>
  balanceBraces(
    repairFlattenedEnvironmentRows(
      normalizeEscapedLatexCommands(
        expression
          .trim()
          .replace(/\\placeholder/g, '')
          .replace(/\\imaginary/g, ''),
      ),
    ).trim(),
  );

/** A command with one backslash marks native LaTeX, where `\\` before a letter is a row break, as in `1\\x-1`. */
const NATIVE_LATEX_COMMAND_PATTERN = /(?<!\\)\\[A-Za-z]/;

/** Normalizes LaTeX extracted from Markdown when its commands were serialized with doubled backslashes. */
export const normalizeMarkdownMathEscapes = (expression: string): string =>
  NATIVE_LATEX_COMMAND_PATTERN.test(expression)
    ? expression
    : expression.replace(MARKDOWN_ESCAPED_LATEX_COMMAND_PATTERN, '\\').replace(MARKDOWN_ESCAPED_ROW_BREAK_PATTERN, '\\\\');

export const normalizeMarkdownMathExpression = (expression: string): string =>
  normalizeLatexExpression(normalizeMarkdownMathEscapes(expression));
